/**
 * Sound Manager Utility
 * Handles preloading and playing of app sounds securely without global click listeners.
 */

const SOUND_PATHS = {
  correct: '/sounds/correct.mp3',
  wrong: '/sounds/incorrect.mp3',
  success: '/sounds/success.mp3',
  fail: '/sounds/fail.mp3',
  streak: '/sounds/streak.mp3'
};

const sounds = {};
let isMuted = false;

// Safely get mute state on load
if (typeof window !== 'undefined') {
  try {
    isMuted = localStorage.getItem('vocab_muted') === 'true';
  } catch (e) {
    // Ignore localStorage errors
  }
}

export const initSounds = () => {
  if (typeof window === 'undefined') return;

  // 1. Preload audio safely without audible playback
  Object.entries(SOUND_PATHS).forEach(([key, path]) => {
    const audio = new Audio(path);
    audio.preload = 'auto';
    audio.volume = 0.4;
    // 2. Reuse persistent Audio instances via singletons
    sounds[key] = audio; 
  });
};

/**
 * Internal helper to safely play a specific sound
 */
const playAudioSafely = (type) => {
  if (isMuted) return;

  const originalSound = sounds[type];
  if (originalSound) {
    try {
      // Clone the preloaded audio node so rapid clicks can overlap without aborting each other
      const soundClone = originalSound.cloneNode();
      soundClone.volume = originalSound.volume;
      
      const playPromise = soundClone.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => {
          // Ignore expected rapid click rejections
        });
      }
    } catch (e) {
      console.error(`Error playing ${type} sound:`, e);
    }
  }
};

// 5. Dedicated sound functions
export const playCorrectSound = () => playAudioSafely('correct');
export const playWrongSound = () => playAudioSafely('wrong');
export const playSuccessSound = () => playAudioSafely('success');
export const playFailSound = () => playAudioSafely('fail');
export const playStreakSound = () => playAudioSafely('streak');

// Legacy generic function for backward compatibility
export const playSound = (type) => playAudioSafely(type);

export const toggleMute = () => {
  isMuted = !isMuted;
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('vocab_muted', String(isMuted));
    } catch (e) {
      // Ignore
    }
  }
  return isMuted;
};

export const getMuteState = () => isMuted;

// Initialize on import if in browser
initSounds();

